import {
  ActionBarPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAui,
  useAuiState,
  useMessage,
} from '@assistant-ui/react'
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  ChatBubbleLeftRightIcon,
  ChevronDoubleRightIcon,
  ChevronRightIcon,
  ClipboardIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  PlusIcon,
  StopIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { TargetBox } from 'components/DragDrop'
import { userChatsAreAlwaysPublic } from 'config'
import { ArtifactsPanel } from 'pages/AiProjects/ArtifactsPanel'
import { ArtifactViewer } from 'pages/AiProjects/ArtifactViewer'
import { RunProjectButton } from 'pages/AiProjects/RunProjectButton'
import {
  Dispatch,
  PointerEvent as ReactPointerEvent,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { AiArtifact, getAiProjectArtifact, openS3Document } from 'services'
import { AssistantPromptFile, AssistantStep, postAssistantUpload } from 'services/apis/ai'
import { AiConversation } from 'services/apis/aiProjects'
import { MarkdownRender } from 'stories/components/MarkdownRender/MarkdownRender'
import { formatTime } from 'utils'

import {
  AI_PANEL_GAP,
  AssistantFloatingPosition,
  AssistantPositionMode,
  clampFloatingPosition,
  DOCUMENTS_PANEL_EVENT,
  DocumentsPanelLayoutDetail,
  loadStoredFloatingPosition,
  writeStoredFloatingPosition,
} from './assistantPanelLayout'
import { AssistantRuntimeProvider, useAssistantAttachments } from './AssistantRuntimeProvider'
import { AssistantStepList, normalizeStep, StepElapsed } from './AssistantSteps'
import {
  ChatExportProvider,
  ChatExportToolbar,
  ExportFormatMenu,
  MessageExportCheckbox,
  persistedMessageId,
  useChatExportOptional,
} from './ChatExportControls'
import { VoiceHeaderControls, VoiceLauncherHalo, VoiceMicButton, VoiceStatusBar } from './voice'

interface AssistantModalProps {
  context?: string
}

/**
 * Kept in the browser so the panel is still open where you left it after a reload. Route changes
 * do not need it - the header this lives in stays mounted as you move around the app - but a hard
 * reload (or a login) remounts everything, and having to reopen the assistant every time defeats
 * the point of a docked panel.
 */
const OPEN_STORAGE_KEY = 'aiAssistantPanelOpen'
const WIDTH_STORAGE_KEY = 'aiAssistantPanelWidth'

/** Panel width, mirrored into --ai-assistant-inset so the page inset matches what it sees. */
const DEFAULT_WIDTH = 480
const MIN_WIDTH = 360
const HISTORY_RAIL_WIDTH = 132
const HISTORY_OPEN_STORAGE_KEY = 'aiAssistantHistoryOpen'
/**
 * The page is inset by the panel, but media queries still see the full window, so a page squeezed
 * far below its desktop layout gets clipped rather than reflowing to a phone layout. Hence the
 * floor on what is left for the app - it is what keeps a wide drag from hiding the screen behind.
 */
const MIN_APP_WIDTH = 480
const FLOATING_PANEL_HEIGHT_VH = 90
const HEADER_DRAG_THRESHOLD = 4

const clampWidth = (width: number) =>
  Math.max(MIN_WIDTH, Math.min(width, Math.max(MIN_WIDTH, window.innerWidth - MIN_APP_WIDTH)))

const floatingPanelHeight = () => (window.innerHeight * FLOATING_PANEL_HEIGHT_VH) / 100

const dragItemToPromptFiles = (item: Record<string, any>): AssistantPromptFile[] => {
  if (Array.isArray(item.documents) && item.documents.length > 0) {
    return item.documents
      .map((doc: Record<string, any>) => ({
        fileKey: doc.fileKey || doc.key,
        name: doc.name,
      }))
      .filter((file: AssistantPromptFile) => file.fileKey && file.name)
  }
  const fileKey = item.fileKey || item.key
  if (!fileKey || !item.name) return []
  return [{ fileKey, name: item.name }]
}

const readStoredOpen = () => {
  try {
    return localStorage.getItem(OPEN_STORAGE_KEY) === '1'
  } catch {
    // Storage can throw in private mode; that only costs the persistence, not the panel.
    return false
  }
}

const readStoredWidth = () => {
  try {
    const stored = Number(localStorage.getItem(WIDTH_STORAGE_KEY))
    return stored > 0 ? clampWidth(stored) : DEFAULT_WIDTH
  } catch {
    return DEFAULT_WIDTH
  }
}

const readStoredHistoryOpen = () => {
  try {
    const stored = localStorage.getItem(HISTORY_OPEN_STORAGE_KEY)
    return stored !== '0'
  } catch {
    return true
  }
}

export function AssistantModal({ context }: AssistantModalProps) {
  const isAuthenticated = useSelector((state: any) => state.auth?.isAuthenticated)
  const [open, setOpen] = useState(() => !!isAuthenticated && readStoredOpen())
  const [width, setWidth] = useState(readStoredWidth)
  const [isResizing, setIsResizing] = useState(false)
  const [positionMode, setPositionMode] = useState<AssistantPositionMode>('docked-right')
  const [floatingPosition, setFloatingPosition] = useState<AssistantFloatingPosition>(() => loadStoredFloatingPosition())
  const [isDragging, setIsDragging] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(readStoredHistoryOpen)
  /**
   * Whether the body shows the project's artifacts instead of the chat. Not persisted, unlike the
   * history rail: reopening the assistant should land you back on the conversation, not on a list.
   */
  const [artifactsOpen, setArtifactsOpen] = useState(false)
  const backToChat = useCallback(() => setArtifactsOpen(false), [])
  const panelRef = useRef<HTMLElement>(null)
  const openRef = useRef(open)
  const positionModeRef = useRef(positionMode)
  const headerDragRef = useRef<{
    startX: number
    startY: number
    startTop: number
    startRight: number
    moved: boolean
  } | null>(null)

  openRef.current = open
  positionModeRef.current = positionMode

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_STORAGE_KEY, open ? '1' : '0')
    } catch {
      // See readStoredOpen.
    }
  }, [open])

  useEffect(() => {
    writeStoredFloatingPosition(floatingPosition)
  }, [floatingPosition])

  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_OPEN_STORAGE_KEY, historyOpen ? '1' : '0')
    } catch {
      // See readStoredOpen.
    }
  }, [historyOpen])

  // Signing out should not leave the panel hanging over the login screen.
  useEffect(() => {
    if (!isAuthenticated) setOpen(false)
  }, [isAuthenticated])

  // The app is inset by the panel instead of covered by it, so the header nav, toasts, and
  // floater tabs stay reachable while the assistant sits open next to them (see index.scss).
  useEffect(() => {
    const inset = open && positionMode === 'docked-right' ? `${width}px` : '0px'
    document.documentElement.style.setProperty('--ai-assistant-inset', inset)
    return () => document.documentElement.style.setProperty('--ai-assistant-inset', '0px')
  }, [open, width, positionMode])

  useEffect(() => {
    const onDocumentsPanel = (event: Event) => {
      const { open: documentsOpen, side, width: documentsWidth } = (event as CustomEvent<DocumentsPanelLayoutDetail>)
        .detail
      if (!documentsOpen || side !== 'right' || !openRef.current) return
      const nextPosition = clampFloatingPosition(
        {
          top: Math.max(48, window.innerHeight * 0.05),
          right: documentsWidth + AI_PANEL_GAP,
        },
        width,
        floatingPanelHeight(),
      )
      setPositionMode('floating')
      setFloatingPosition(nextPosition)
    }
    window.addEventListener(DOCUMENTS_PANEL_EVENT, onDocumentsPanel)
    return () => window.removeEventListener(DOCUMENTS_PANEL_EVENT, onDocumentsPanel)
  }, [width])

  useEffect(() => {
    const onWindowResize = () => {
      setWidth((current) => clampWidth(current))
      setFloatingPosition((current) => clampFloatingPosition(current, width, floatingPanelHeight()))
    }
    window.addEventListener('resize', onWindowResize)
    return () => window.removeEventListener('resize', onWindowResize)
  }, [width])

  // Dragging the edge has to feel attached to the cursor, so the eased page inset is switched off
  // for the duration of the drag rather than chasing it a third of a second behind.
  useEffect(() => {
    document.documentElement.classList.toggle('ai-assistant-resizing', isResizing)
    document.documentElement.classList.toggle('ai-assistant-dragging', isDragging)
    document.body.style.cursor = isResizing ? 'col-resize' : isDragging ? 'grabbing' : ''
    return () => {
      document.documentElement.classList.remove('ai-assistant-resizing')
      document.documentElement.classList.remove('ai-assistant-dragging')
      document.body.style.cursor = ''
    }
  }, [isResizing, isDragging])

  const dockToRight = useCallback(() => {
    setPositionMode('docked-right')
  }, [])

  /** FAB / voice wake always open docked on the right; dragging the header switches to floating. */
  const openAssistant = useCallback(() => {
    setPositionMode('docked-right')
    setOpen(true)
  }, [])

  const onHeaderPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return
    if ((event.target as HTMLElement).closest('button, a, select, input, textarea')) return
    const startTop = positionMode === 'floating' ? floatingPosition.top : 0
    const startRight = positionMode === 'floating' ? floatingPosition.right : 0
    headerDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startTop,
      startRight,
      moved: false,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onHeaderPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const drag = headerDragRef.current
    if (!drag) return
    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    if (!drag.moved && Math.abs(dx) < HEADER_DRAG_THRESHOLD && Math.abs(dy) < HEADER_DRAG_THRESHOLD) return

    if (!drag.moved && positionModeRef.current === 'docked-right') {
      const rect = panelRef.current?.getBoundingClientRect()
      if (rect) {
        drag.startTop = rect.top
        drag.startRight = window.innerWidth - rect.right
      }
      setPositionMode('floating')
    }

    drag.moved = true
    setIsDragging(true)
    setFloatingPosition(
      clampFloatingPosition(
        {
          top: drag.startTop + dy,
          right: drag.startRight - dx,
        },
        width,
        floatingPanelHeight(),
      ),
    )
  }

  const endHeaderDrag = (event: React.PointerEvent<HTMLElement>) => {
    const drag = headerDragRef.current
    headerDragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (drag?.moved) setIsDragging(false)
  }

  const resizeTo = useCallback((next: number) => {
    const clamped = clampWidth(next)
    setWidth(clamped)
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(clamped))
    } catch {
      // See readStoredOpen.
    }
  }, [])

  // The panel stays mounted whether or not it is open - that is what keeps the conversation alive
  // while you work - so focus has to be moved on open rather than with autoFocus, which would
  // otherwise grab the keyboard on every page load.
  useEffect(() => {
    if (!open) return
    const timer = window.setTimeout(() => panelRef.current?.querySelector('textarea')?.focus(), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  const isFloating = positionMode === 'floating'
  const panelMotionClass =
    isResizing || isDragging ? '' : 'transition-all duration-300 ease-in-out'
  const panelVisibilityClass = open
    ? 'translate-x-0 opacity-100 visible'
    : isFloating
      ? 'pointer-events-none opacity-0 invisible'
      : 'translate-x-full pointer-events-none'

  return (
    <AssistantRuntimeProvider
      context={context}
      voiceEnabled={!!isAuthenticated}
      onVoiceWake={openAssistant}
    >
      <AssistantPanelShell
        open={open}
        setOpen={setOpen}
        openAssistant={openAssistant}
        panelRef={panelRef}
        isFloating={isFloating}
        width={width}
        floatingPosition={floatingPosition}
        panelMotionClass={panelMotionClass}
        panelVisibilityClass={panelVisibilityClass}
        isDragging={isDragging}
        isResizing={isResizing}
        setIsResizing={setIsResizing}
        resizeTo={resizeTo}
        onHeaderPointerDown={onHeaderPointerDown}
        onHeaderPointerMove={onHeaderPointerMove}
        endHeaderDrag={endHeaderDrag}
        dockToRight={dockToRight}
        positionMode={positionMode}
        historyOpen={historyOpen}
        setHistoryOpen={setHistoryOpen}
        artifactsOpen={artifactsOpen}
        setArtifactsOpen={setArtifactsOpen}
        backToChat={backToChat}
      />
    </AssistantRuntimeProvider>
  )
}

/**
 * Lives under AssistantRuntimeProvider so export can read the active conversation and loan, and so
 * the thread toolbar can list the persisted message ids on screen.
 */
function AssistantPanelShell({
  open,
  setOpen,
  openAssistant,
  panelRef,
  isFloating,
  width,
  floatingPosition,
  panelMotionClass,
  panelVisibilityClass,
  isDragging,
  isResizing,
  setIsResizing,
  resizeTo,
  onHeaderPointerDown,
  onHeaderPointerMove,
  endHeaderDrag,
  dockToRight,
  positionMode,
  historyOpen,
  setHistoryOpen,
  artifactsOpen,
  setArtifactsOpen,
  backToChat,
}: {
  open: boolean
  setOpen: (value: boolean) => void
  openAssistant: () => void
  panelRef: RefObject<HTMLElement>
  isFloating: boolean
  width: number
  floatingPosition: AssistantFloatingPosition
  panelMotionClass: string
  panelVisibilityClass: string
  isDragging: boolean
  isResizing: boolean
  setIsResizing: (value: boolean) => void
  resizeTo: (width: number) => void
  onHeaderPointerDown: (event: ReactPointerEvent) => void
  onHeaderPointerMove: (event: ReactPointerEvent) => void
  endHeaderDrag: (event: ReactPointerEvent) => void
  dockToRight: () => void
  positionMode: AssistantPositionMode
  historyOpen: boolean
  setHistoryOpen: Dispatch<SetStateAction<boolean>>
  artifactsOpen: boolean
  setArtifactsOpen: Dispatch<SetStateAction<boolean>>
  backToChat: () => void
}) {
  const { activeConversationId, loanNumber, isRunning, conversations } = useAssistantAttachments()
  const threadLoan =
    conversations.find((item) => item.id === activeConversationId)?.loanNumber || loanNumber || undefined

  return (
    <ChatExportProvider conversationId={activeConversationId} loanNumber={threadLoan || undefined} isRunning={isRunning}>
      {/* No backdrop: the panel is a dock, not a modal. The rest of the app stays clickable so you
          can navigate, open loans, and keep the same conversation going alongside. */}
      <aside
        ref={panelRef as RefObject<HTMLAsideElement>}
        aria-hidden={!open}
        aria-label="AI Assistant"
        style={
          isFloating
            ? {
                width,
                top: floatingPosition.top,
                right: floatingPosition.right,
                height: `${FLOATING_PANEL_HEIGHT_VH}vh`,
              }
            : { width }
        }
        className={`fixed z-50 flex max-w-[calc(100vw-2rem)] flex-col border border-gray-200 bg-white shadow-2xl ${panelMotionClass} ${panelVisibilityClass} ${
          isFloating ? 'rounded-lg overflow-hidden' : 'top-0 right-0 h-full border-l shadow-2xl'
        } ${isDragging ? 'select-none' : ''}`}
      >
        {!isFloating && (
          <ResizeGrip
            open={open}
            width={width}
            isResizing={isResizing}
            setIsResizing={setIsResizing}
            resizeTo={resizeTo}
          />
        )}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-shade-blue text-white">
          <div
            className={`flex min-w-0 flex-1 items-center gap-2 px-4 py-3 ${
              isDragging ? 'cursor-grabbing' : positionMode === 'floating' ? 'cursor-grab' : ''
            }`}
            title={
              positionMode === 'floating'
                ? 'Drag to move. Double-click to dock on the right.'
                : 'Drag to detach and move this panel.'
            }
            style={{ touchAction: 'none' }}
            onPointerDown={onHeaderPointerDown}
            onPointerMove={onHeaderPointerMove}
            onPointerUp={endHeaderDrag}
            onPointerCancel={endHeaderDrag}
            onDoubleClick={dockToRight}
          >
            <ComputerDesktopIcon className="h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold leading-tight">AI Assistant</h3>
              <ModelBadge />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 px-2">
            <button
              className={`rounded-md p-1 transition-colors ${historyOpen ? 'bg-white/20' : 'hover:bg-white/20'}`}
              aria-label={historyOpen ? 'Hide chat history' : 'Show chat history'}
              aria-pressed={historyOpen}
              tabIndex={open ? 0 : -1}
              title={historyOpen ? 'Hide chat history' : 'Show chat history'}
              onClick={() => setHistoryOpen((current) => !current)}
            >
              <Bars3Icon className="h-4 w-4" />
            </button>
            <ArtifactsToggle
              open={open}
              isShowing={artifactsOpen}
              onToggle={() => setArtifactsOpen((current) => !current)}
            />
            {isFloating && (
              <button
                className="rounded-md p-1 hover:bg-white/20 transition-colors"
                aria-label="Dock to right edge"
                tabIndex={open ? 0 : -1}
                title="Dock to right edge"
                onClick={dockToRight}
              >
                <ChevronDoubleRightIcon className="h-4 w-4" />
              </button>
            )}
            <HeaderVoiceControls open={open} />
            <button
              className="rounded-md p-1 hover:bg-white/20 transition-colors"
              aria-label="Close"
              tabIndex={open ? 0 : -1}
              onClick={() => setOpen(false)}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1">
          {historyOpen && <ConversationHistoryRail width={HISTORY_RAIL_WIDTH} />}
          <AssistantPanelDropZone className="min-w-0 flex-1">
            <WorkingContext />
            {artifactsOpen ? (
              <ArtifactsView onBackToChat={backToChat} />
            ) : (
              <ThreadPrimitive.Root className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <ThreadPrimitive.Viewport className="min-h-0 flex-1 overflow-y-auto px-4 py-3 space-y-4">
                  <ThreadWelcome />
                  <ThreadPrimitive.Messages components={{ UserMessage, AssistantMessage }} />
                  <RunningIndicator />
                </ThreadPrimitive.Viewport>
                <VoiceStatus />
                <Composer />
              </ThreadPrimitive.Root>
            )}
          </AssistantPanelDropZone>
        </div>
      </aside>
      <VoiceHotkeys />
      <button
        onClick={openAssistant}
        className={`fixed bottom-16 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-shade-blue text-white shadow-lg hover:bg-shade-blue/90 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-shade-blue/50 focus:ring-offset-2 ${
          open ? 'pointer-events-none translate-x-6 opacity-0' : 'translate-x-0 opacity-100'
        }`}
        tabIndex={open ? -1 : 0}
        aria-label="Open AI Assistant"
      >
        <ChatBubbleLeftRightIcon className="h-6 w-6" />
        <LauncherHalo />
      </button>
    </ChatExportProvider>
  )
}

/**
 * Voice lives in the runtime provider so the microphone survives navigation the same way the
 * conversation does. These three read it back out of context - the panel shell itself renders
 * the provider, so it cannot use the hook directly.
 */
function HeaderVoiceControls({ open }: { open: boolean }) {
  const { voice } = useAssistantAttachments()
  return <VoiceHeaderControls voice={voice} open={open} />
}

function VoiceStatus() {
  const { voice } = useAssistantAttachments()
  return <VoiceStatusBar voice={voice} />
}

function LauncherHalo() {
  const { voice } = useAssistantAttachments()
  return <VoiceLauncherHalo voice={voice} />
}

/**
 * Push-to-talk and a way out, bound globally: the point of voice is not having to find the panel
 * first. Escape only acts while a voice turn is in flight, so it still closes dialogs otherwise.
 */
function VoiceHotkeys() {
  const { voice } = useAssistantAttachments()
  const { available, state, startListening, cancel } = voice

  useEffect(() => {
    if (!available) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (state === 'listening' || state === 'thinking' || state === 'speaking') cancel()
        return
      }
      if (event.shiftKey && (event.metaKey || event.ctrlKey) && event.code === 'KeyV') {
        event.preventDefault()
        startListening()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [available, state, startListening, cancel])

  return null
}

interface ResizeGripProps {
  open: boolean
  width: number
  isResizing: boolean
  setIsResizing: (value: boolean) => void
  resizeTo: (width: number) => void
}

/**
 * The panel's left edge, dragged to set how much of the screen the assistant takes. Pointer capture
 * keeps the drag alive over iframes and grids that would otherwise swallow the move events, and
 * double-clicking puts the width back to the default the way the floater tabs reset on right-click.
 */
function ResizeGrip({ open, width, isResizing, setIsResizing, resizeTo }: ResizeGripProps) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize AI Assistant"
      aria-valuenow={width}
      tabIndex={open ? 0 : -1}
      title="Drag to resize. Double-click to reset."
      className={`absolute inset-y-0 left-0 z-10 w-1.5 -ml-0.5 cursor-col-resize transition-colors hover:bg-shade-blue/40 focus:outline-none focus-visible:bg-shade-blue/40 ${
        isResizing ? 'bg-shade-blue/40' : 'bg-transparent'
      }`}
      style={{ touchAction: 'none' }}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId)
        setIsResizing(true)
      }}
      onPointerMove={(event) => {
        if (!isResizing) return
        resizeTo(window.innerWidth - event.clientX)
      }}
      onPointerUp={(event) => {
        event.currentTarget.releasePointerCapture(event.pointerId)
        setIsResizing(false)
      }}
      onPointerCancel={() => setIsResizing(false)}
      onDoubleClick={() => resizeTo(DEFAULT_WIDTH)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') resizeTo(width + 24)
        else if (event.key === 'ArrowRight') resizeTo(width - 24)
        else return
        event.preventDefault()
      }}
    />
  )
}

/**
 * Header toggle for the artifacts list, with the number of documents in reach.
 *
 * Hidden without a project loaded, because an artifact always belongs to one - there would be
 * nothing to list and the button would only ever open an empty panel.
 */
function ArtifactsToggle({
  open,
  isShowing,
  onToggle,
}: {
  open: boolean
  isShowing: boolean
  onToggle: () => void
}) {
  const { projectId, artifacts } = useAssistantAttachments()
  if (!projectId) return null

  const label = isShowing ? 'Back to the chat' : `Artifacts (${artifacts.length})`
  return (
    <button
      className={`relative rounded-md p-1 transition-colors ${isShowing ? 'bg-white/20' : 'hover:bg-white/20'}`}
      aria-label={label}
      aria-pressed={isShowing}
      tabIndex={open ? 0 : -1}
      title={label}
      onClick={onToggle}
    >
      <DocumentTextIcon className="h-4 w-4" />
      {!!artifacts.length && (
        <span className="absolute -right-0.5 -top-0.5 min-w-[14px] rounded-full bg-white px-1 text-[9px] font-bold leading-[14px] text-shade-blue">
          {artifacts.length > 9 ? '9+' : artifacts.length}
        </span>
      )}
    </button>
  )
}

/**
 * The project's artifacts for where you are standing: on a loan, that loan's documents plus the
 * project's unscoped ones, which is what the server returns for a loan-scoped request.
 *
 * This is the same list and the same viewer as the AI Projects workspace, mounted in the panel so
 * a report the assistant just saved can be read without leaving the loan.
 */
function ArtifactsView({ onBackToChat }: { onBackToChat: () => void }) {
  const { projectId, loanNumber, artifacts, reloadArtifacts } = useAssistantAttachments()
  const [openArtifact, setOpenArtifact] = useState<AiArtifact | null>(null)

  // Unloading the project while this is open leaves nothing to show, and a create would post to
  // project 0. Fall back to the chat, which is also where the now-hidden toggle would send you.
  useEffect(() => {
    if (!projectId) onBackToChat()
  }, [projectId, onBackToChat])

  if (!projectId) return null

  // The list omits `content` so it stays cheap; the viewer needs the body, hence the second read.
  const onOpen = async (artifactId: number) => {
    const artifact = await getAiProjectArtifact(projectId, artifactId).catch(() => null)
    if (artifact) setOpenArtifact(artifact)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
        <p className="truncate text-xs font-semibold text-gray-700">
          Artifacts
          {!!loanNumber && <span className="font-normal text-gray-500"> · loan {loanNumber}</span>}
        </p>
        <button className="shrink-0 text-xs text-shade-blue hover:underline" onClick={onBackToChat}>
          Back to chat
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <ArtifactsPanel
          projectId={projectId}
          loanNumber={loanNumber || undefined}
          artifacts={artifacts}
          onChanged={reloadArtifacts}
          onOpen={onOpen}
        />
      </div>
      {/* Portaled to the body on purpose. The panel carries `translate-x-0`, and a transformed
          ancestor becomes the containing block for `position: fixed`, so the viewer's `inset-0`
          would resolve against the 480px panel instead of the viewport - and `overflow-hidden`
          would clip it outright while the panel is floating. */}
      {openArtifact &&
        createPortal(
          <ArtifactViewer
            projectId={projectId}
            artifact={openArtifact}
            onClose={() => setOpenArtifact(null)}
            onSaved={() => {
              reloadArtifacts()
              onOpen(openArtifact.id)
            }}
          />,
          document.body,
        )}
    </div>
  )
}

/**
 * What this chat is working on: which project is loaded, and which loan (if any) it is pointed at.
 *
 * Loading a project here brings its instructions, knowledge, and artifact tools into the floating
 * assistant. Conversations are saved automatically and listed in the history rail.
 */
function WorkingContext() {
  const { projects, projectId, setProjectId, loanNumber, selectConversation, sendPrompt, isRunning, messages } =
    useAssistantAttachments()
  const exportCtx = useChatExportOptional()
  const persistedIds = messages.map((message) => persistedMessageId(message.id)).filter((id) => id > 0)

  if (!projects.length && !loanNumber && !exportCtx) return null

  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50 shrink-0">
      {projects.length > 0 && (
        <select
          className="min-w-0 flex-1 text-xs rounded-md border border-gray-300 bg-white px-2 py-1 text-gray-700 focus:border-shade-blue focus:outline-none"
          value={projectId || ''}
          onChange={(event) => setProjectId(Number(event.target.value) || 0)}
          aria-label="AI project"
          title="Load a project's instructions and knowledge into this chat"
        >
          <option value="">No project</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      )}
      {!!projectId && (
        <RunProjectButton
          projectId={projectId}
          loanNumber={loanNumber || undefined}
          variant="compact"
          onOpenConversation={selectConversation}
          // Run the project here in the panel, the way a typed turn runs: this chat already carries
          // the project and the loan.
          onRunProject={(prompt) => sendPrompt(prompt).then(() => undefined)}
          isHostBusy={isRunning}
        />
      )}
      {!!loanNumber && (
        <span
          className="shrink-0 rounded-full bg-shade-blue/10 text-shade-blue text-[11px] font-semibold px-2 py-1"
          title="Answers and actions default to this loan"
        >
          Loan {loanNumber}
        </span>
      )}
      {!!loanNumber && projects.length > 0 && (
        <Link
          to={`/loan_ai_project/${loanNumber}`}
          className="shrink-0 p-1 rounded text-gray-500 hover:text-shade-blue hover:bg-white"
          title="Open the full project workspace for this loan"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </Link>
      )}
      {exportCtx && <ChatExportToolbar allMessageIds={persistedIds} />}
    </div>
  )
}

// The AI model the assistant answers with, shown under the header title. Hidden until loaded.
function ModelBadge() {
  const { model } = useAssistantAttachments()
  if (!model) return null
  return <p className="truncate text-[10px] font-normal leading-tight text-white/70">{model}</p>
}

function RunningIndicator() {
  const { isRunning, runningStatus, liveSteps, streamingText } = useAssistantAttachments()
  if (!isRunning) return null
  // Everything except the step in flight; that one is the bouncing line below, so showing it twice
  // would just read as a stutter.
  const done = liveSteps.slice(0, -1)
  return (
    <div className="px-1 text-gray-500">
      {done.length > 0 && <AssistantStepList steps={done} className="mb-1.5" />}
      {streamingText ? (
        // The answer as it is written. The dots below are for the wait before the first word; once
        // there are words, they are the better progress indicator, and keeping both reads as noise.
        <div className="text-sm text-gray-800">
          <MarkdownRender text={streamingText} />
          <span className="ml-0.5 inline-block h-4 w-[3px] animate-pulse rounded-sm bg-shade-blue/70 align-text-bottom" />
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <span className="h-2 w-2 animate-bounce rounded-full bg-shade-blue/60 [animation-delay:-0.3s]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-shade-blue/60 [animation-delay:-0.15s]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-shade-blue/60" />
          </div>
          <span className="text-sm transition-opacity">{runningStatus || 'Working on it…'}</span>
          {/* Counts up while the phrase stands, so a long silence reads as work, not a stall. */}
          <StepElapsed resetKey={runningStatus} className="text-xs text-gray-400" />
        </div>
      )}
    </div>
  )
}

function ThreadWelcome() {
  const { loanNumber, voice } = useAssistantAttachments()
  return (
    <ThreadPrimitive.Empty>
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-shade-blue/10">
          <ComputerDesktopIcon className="h-7 w-7 text-shade-blue" />
        </div>
        <h4 className="text-base font-semibold text-gray-800">How can I help?</h4>
        <p className="mt-1 text-sm text-gray-500 max-w-[260px]">
          {loanNumber
            ? `Ask me anything about loan ${loanNumber} - or load a project to work it with your own instructions and documents.`
            : 'Ask me anything about your loans, documents, or processes.'}
        </p>
        <p className="mt-2 text-xs text-gray-400 max-w-[260px]">
          Drag documents from the Documents tab here to attach them to your next message.
        </p>
        {voice.available && (
          <p className="mt-2 text-xs text-gray-400 max-w-[260px]">
            {voice.wakeEnabled
              ? `Listening. Just say "${voice.wakePhrase}".`
              : `Turn on "${voice.wakePhrase}" above to ask out loud, or press the microphone to speak.`}
          </p>
        )}
      </div>
    </ThreadPrimitive.Empty>
  )
}

const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.xlsx,.docx'

const isAcceptedFile = (name: string) => {
  const lower = name.toLowerCase()
  return ACCEPTED_FILE_TYPES.split(',').some((ext) => lower.endsWith(ext.trim()))
}

// Clipboard images arrive with no useful name: a screenshot tool gives an unnamed blob and Chrome
// calls every one of them "image.png", so several pasted shots would be indistinguishable in the
// attachment list. Name them by paste order and time instead, keeping the extension the MIME type
// implies (and only the ones the upload accepts - a pasted GIF or SVG has no extension here).
const PASTED_IMAGE_EXTENSIONS: Record<string, string> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
}

const namePastedFile = (file: File, index: number) => {
  const extension = PASTED_IMAGE_EXTENSIONS[file.type]
  if (!extension || (file.name && file.name.toLowerCase() !== `image${extension}`)) return file
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  const stamp =
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
  const suffix = index > 0 ? `-${index + 1}` : ''
  return new File([file], `pasted-${stamp}${suffix}${extension}`, { type: file.type })
}

// Rich text carries BOTH text and a picture of itself: copying from Word, Excel or a web page puts
// an image on the clipboard alongside the text. The text is what was meant, so a paste that has any
// real text stays a text paste and only a picture-only clipboard becomes an attachment.
const pastedFiles = (clipboard: DataTransfer | null) => {
  if (!clipboard || clipboard.getData('text/plain').trim()) return []
  return Array.from(clipboard.files)
    .filter((file) => isAcceptedFile(file.name) || !!PASTED_IMAGE_EXTENSIONS[file.type])
    .map(namePastedFile)
}

// Upload, then attach. Shared by the paperclip, a drop from the desktop and a paste.
function useUploadAndAttach() {
  const { addFiles, setIsUploading } = useAssistantAttachments()

  return async (files: File[]) => {
    if (files.length === 0) return
    setIsUploading(true)
    try {
      const response = await postAssistantUpload(files)
      if (response?.data?.length) addFiles(response.data)
    } catch {
      // Upload errors are surfaced by the global API handler.
    } finally {
      setIsUploading(false)
    }
  }
}

function DraftModeToggle() {
  const { draftMode, setDraftMode } = useAssistantAttachments()
  const options: { value: 'values' | 'placeholders'; label: string; title: string }[] = [
    {
      value: 'values',
      label: 'Real values',
      title: 'Drafted notes and emails fill in actual loan data (borrower, amounts, dates).',
    },
    {
      value: 'placeholders',
      label: 'Placeholders',
      title: 'Drafted notes and emails leave a reusable template, e.g. [Borrower Name].',
    },
  ]
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <span className="text-[11px] font-medium text-gray-400">Drafts use</span>
      <div className="flex rounded-md border border-gray-200 bg-white p-0.5">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            title={option.title}
            aria-pressed={draftMode === option.value}
            onClick={() => setDraftMode(option.value)}
            className={`rounded px-2 py-0.5 text-[11px] font-medium transition-colors ${
              draftMode === option.value ? 'bg-shade-blue text-white' : 'text-gray-500 hover:text-shade-blue'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function ConversationHistoryRail({ width }: { width: number }) {
  const {
    conversations,
    activeConversationId,
    conversationsLoading,
    startNewConversation,
    selectConversation,
    deleteConversation,
    setConversationVisibility,
    isRunning,
  } = useAssistantAttachments()
  const profile = useSelector((state: any) => state.auth?.profile)
  const userEmail = profile?.email || ''
  const chatsStayPublic = userChatsAreAlwaysPublic(profile)

  const onDelete = (event: React.MouseEvent, conversation: AiConversation) => {
    event.stopPropagation()
    deleteConversation(conversation)
  }

  const onToggleVisibility = (event: React.MouseEvent, conversation: AiConversation) => {
    event.stopPropagation()
    setConversationVisibility(conversation, conversation.visibility === 'private' ? 'public' : 'private').catch(() => {})
  }

  return (
    <aside
      className="flex shrink-0 flex-col border-r border-gray-200 bg-gray-50 min-h-0"
      style={{ width }}
      aria-label="Chat history"
    >
      <div className="border-b border-gray-200 p-2">
        <button
          type="button"
          className="flex w-full items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 hover:border-shade-blue hover:text-shade-blue disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => startNewConversation()}
          disabled={isRunning}
        >
          <PlusIcon className="h-3.5 w-3.5" />
          New chat
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
        {conversationsLoading && !conversations.length && (
          <p className="px-2 py-3 text-[11px] text-gray-400">Loading…</p>
        )}
        {!conversationsLoading && !conversations.length && (
          <p className="px-2 py-3 text-[11px] text-gray-400">No chats yet.</p>
        )}
        {conversations.map((conversation) => {
          const isActive = conversation.id === activeConversationId
          const isOwner = conversation.createdBy === userEmail
          const isPrivate = conversation.visibility === 'private'
          return (
            <div
              key={conversation.id}
              className={`group flex cursor-pointer items-start gap-1 rounded-md px-1.5 py-1.5 ${
                isActive ? 'bg-shade-blue/10' : 'hover:bg-white'
              }`}
              onClick={() => selectConversation(conversation.id)}
              title={conversation.title}
            >
              <ChatBubbleLeftRightIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] font-medium text-gray-800">{conversation.title}</p>
                <p className="truncate text-[10px] text-gray-400">
                  {formatTime(conversation.lastMessageAt || conversation.createdAt)}
                  {!isOwner && !!conversation.createdBy && ` · ${conversation.createdBy.split('@')[0]}`}
                </p>
              </div>
              {isOwner ? (
                <>
                  {chatsStayPublic ? (
                    <span
                      className="mt-0.5 shrink-0 p-0.5 text-gray-400"
                      title="Public - chats from your account stay visible to your team."
                    >
                      <GlobeAltIcon className="h-3 w-3" />
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={`mt-0.5 shrink-0 p-0.5 hover:text-shade-blue ${
                        isPrivate ? 'text-gray-500' : 'text-gray-400 opacity-0 group-hover:opacity-100'
                      }`}
                      title={isPrivate ? 'Private - only you can see this chat. Click to make it public.' : 'Public - your team can see this chat. Click to make it private.'}
                      onClick={(event) => onToggleVisibility(event, conversation)}
                    >
                      {isPrivate ? <LockClosedIcon className="h-3 w-3" /> : <GlobeAltIcon className="h-3 w-3" />}
                    </button>
                  )}
                  <button
                    type="button"
                    className="mt-0.5 shrink-0 p-0.5 text-gray-400 opacity-0 hover:text-red-600 group-hover:opacity-100"
                    title="Delete conversation"
                    onClick={(event) => onDelete(event, conversation)}
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </>
              ) : (
                <span className="mt-0.5 shrink-0 p-0.5 text-gray-300" title={`Shared by ${conversation.createdBy}`}>
                  <GlobeAltIcon className="h-3 w-3" />
                </span>
              )}
            </div>
          )
        })}
      </div>
    </aside>
  )
}

function AssistantPanelDropZone({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const { addFiles } = useAssistantAttachments()
  const uploadAndAttach = useUploadAndAttach()
  const [isFileOver, setIsFileOver] = useState(false)
  const fileDragDepth = useRef(0)

  // Internal app items (dragging an existing loan document into the panel) are already-uploaded S3
  // docs, so attach them directly.
  const onDrop = (item: Record<string, any>) => {
    const files = dragItemToPromptFiles(item)
    if (files.length) addFiles(files)
  }

  // External OS files (dragged from the desktop) are NOT yet uploaded, so upload them like the
  // paperclip does before attaching. TargetBox handles 'document' items; these native handlers handle
  // raw files. dragDepth counters nested dragenter/leave so the overlay does not flicker over children.
  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files')
  const onFileDragEnter = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return
    e.preventDefault()
    fileDragDepth.current += 1
    setIsFileOver(true)
  }
  const onFileDragOver = (e: React.DragEvent) => {
    if (isFileDrag(e)) e.preventDefault() // required so the drop event fires
  }
  const onFileDragLeave = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return
    fileDragDepth.current -= 1
    if (fileDragDepth.current <= 0) {
      fileDragDepth.current = 0
      setIsFileOver(false)
    }
  }
  const onFileDrop = async (e: React.DragEvent) => {
    if (!isFileDrag(e)) return
    e.preventDefault()
    fileDragDepth.current = 0
    setIsFileOver(false)
    const accepted = Array.from(e.dataTransfer?.files || []).filter((file) => isAcceptedFile(file.name))
    await uploadAndAttach(accepted)
  }

  // Paste a screenshot straight in instead of saving it to disk and picking it up with the
  // paperclip. The handler sits on the panel rather than the message box so a paste still lands
  // while focus is on the transcript or a button; paste events bubble, so the message box is
  // covered too. Focus outside the panel is the browser's business, not ours.
  const onFilePaste = async (e: React.ClipboardEvent) => {
    const files = pastedFiles(e.clipboardData)
    if (files.length === 0) return // plain text: let it paste into the message box as usual
    e.preventDefault()
    await uploadAndAttach(files)
  }

  return (
    <div
      className={`relative flex min-h-0 flex-1 flex-col ${className}`.trim()}
      onDragEnter={onFileDragEnter}
      onDragOver={onFileDragOver}
      onDragLeave={onFileDragLeave}
      onDrop={onFileDrop}
      onPaste={onFilePaste}
    >
      {isFileOver && (
        <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-shade-blue bg-shade-blue/10">
          <PaperClipIcon className="h-8 w-8 text-shade-blue" />
          <p className="text-sm font-medium text-shade-blue">Drop file to attach</p>
        </div>
      )}
      <TargetBox
        types={['document']}
        onDrop={onDrop}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        highlightClassName="outline-[3px] outline-shade-blue/70 outline-dashed z-9 relative bg-shade-blue/5"
      >
        {children}
      </TargetBox>
    </div>
  )
}

/**
 * Messages sent while the assistant is still answering. They are not part of the thread yet, so
 * they sit here until their turn comes; each can be removed before it goes.
 */
function QueuedPrompts() {
  const { queuedPrompts, removeQueuedPrompt } = useAssistantAttachments()
  if (!queuedPrompts.length) return null
  return (
    <div className="mb-2 flex flex-col gap-1" aria-label="Queued messages">
      <span className="text-[11px] font-medium text-gray-400">
        Queued - sends after this answer ({queuedPrompts.length})
      </span>
      {queuedPrompts.map((item) => (
        <div
          key={item.id}
          className="flex max-w-full items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-2 py-1 text-xs text-gray-600"
        >
          <span className="truncate">{item.text || `${item.files.length} attached file(s)`}</span>
          <button
            type="button"
            className="ml-auto shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Remove queued message"
            onClick={() => removeQueuedPrompt(item.id)}
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  )
}

/** Sends the composer's message into the queue while a turn runs - the Send button's job then. */
function QueueSendButton() {
  const aui = useAui()
  const isEmpty = useAuiState((s) => s.composer.isEmpty)
  const { isUploading } = useAssistantAttachments()
  return (
    <button
      type="button"
      title="Queue message - it sends when this answer finishes"
      aria-label="Queue message"
      disabled={isEmpty || isUploading}
      onClick={() => aui.composer().send()}
      className="m-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-shade-blue text-white hover:bg-shade-blue/90 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
    >
      <PaperAirplaneIcon className="h-4 w-4" />
    </button>
  )
}

function Composer() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { files, removeFile, isUploading, voice, isRunning } = useAssistantAttachments()
  const uploadAndAttach = useUploadAndAttach()
  const aui = useAui()

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || [])
    event.target.value = ''
    await uploadAndAttach(selectedFiles)
  }

  return (
    <ComposerPrimitive.Root className="border-t border-gray-200 bg-gray-50 px-3 py-3 shrink-0">
      <DraftModeToggle />
      <QueuedPrompts />
      {files.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {files.map((file) => (
            <div
              key={file.fileKey}
              className="flex max-w-full items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
            >
              <PaperClipIcon className="h-3 w-3 shrink-0 text-gray-400" />
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label={`Remove ${file.name}`}
                onClick={() => removeFile(file.fileKey)}
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="relative flex items-end rounded-lg border border-gray-300 bg-white focus-within:border-shade-blue focus-within:ring-1 focus-within:ring-shade-blue/30 transition-all">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_FILE_TYPES}
          className="hidden"
          onChange={handleFileSelect}
        />
        <button
          type="button"
          className="m-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-shade-blue disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          aria-label="Attach files"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
          ) : (
            <PaperClipIcon className="h-4 w-4" />
          )}
        </button>
        <VoiceMicButton voice={voice} disabled={isUploading} />
        {/* The textarea cannot be the flex item itself. Its autosizer reads its own width to decide
            where to wrap, and as a flex item that width sometimes collapses to a few characters, so
            the box draws as a thin column. The wrapper takes the leftover width; the textarea fills it. */}
        <div className="min-w-0 flex-1">
          <ComposerPrimitive.Input
            placeholder={isRunning ? 'Keep typing - your next message will queue' : 'Type a message...'}
            rows={1}
            className="block w-full min-w-0 resize-none border-0 bg-transparent px-1 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 max-h-24 overflow-y-auto"
            onKeyDown={(event) => {
              // The primitive ignores Enter while a turn runs. Here it queues the message instead, so
              // the user can keep working while the answer is still being written.
              if (!isRunning || event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
              event.preventDefault()
              if (!aui.composer().getState().isEmpty && !isUploading) aui.composer().send()
            }}
          />
        </div>
        {isRunning ? (
          // Stop sits beside the queue button rather than replacing Send, so a message can be sent
          // while a turn runs. Cancelling keeps what has been written so far, on screen and in the thread.
          <ComposerPrimitive.Cancel asChild>
            <button
              title="Stop"
              aria-label="Stop generating"
              className="m-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-800 text-white hover:bg-gray-700 transition-colors"
            >
              <StopIcon className="h-4 w-4" />
            </button>
          </ComposerPrimitive.Cancel>
        ) : null}
        {isRunning ? (
          <QueueSendButton />
        ) : (
          <ComposerPrimitive.Send asChild>
            <button
              disabled={isUploading}
              className="m-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-shade-blue text-white hover:bg-shade-blue/90 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          </ComposerPrimitive.Send>
        )}
      </div>
    </ComposerPrimitive.Root>
  )
}

function UserMessageAttachments() {
  const files = useMessage(
    (message) => message.metadata?.custom?.files as AssistantPromptFile[] | undefined,
  )

  if (!files?.length) return null

  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {files.map((file) => (
        <button
          key={file.fileKey}
          type="button"
          title={`Download ${file.name}`}
          onClick={() => openS3Document(file.fileKey, true)}
          className="flex max-w-full items-center gap-1 rounded-md border border-shade-blue/30 bg-white px-2 py-1 text-xs text-shade-blue hover:bg-shade-blue/5 transition-colors"
        >
          <PaperClipIcon className="h-3 w-3 shrink-0" />
          <span className="truncate">{file.name}</span>
        </button>
      ))}
    </div>
  )
}

function UserMessage() {
  const messageId = persistedMessageId(useMessage((message) => message.id))
  return (
    <MessagePrimitive.Root className="flex gap-2 justify-end">
      <MessageExportCheckbox messageId={messageId} />
      <div className="max-w-[85%] flex flex-col items-end gap-1.5">
        <UserMessageAttachments />
        <div className="rounded-2xl rounded-tr-sm bg-shade-blue px-3.5 py-2 text-sm text-white">
          <MessagePrimitive.Content
            components={{
              Text: ({ text }) => <span className="whitespace-pre-wrap">{text}</span>,
            }}
          />
        </div>
      </div>
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-600">
        <UserIcon className="h-4 w-4" />
      </div>
    </MessagePrimitive.Root>
  )
}

// The ordered list of what the assistant did this turn (from the live status stream), collapsed
// by default so the reader can expand to see every step, the way Claude shows its work.
function AssistantMessageSteps() {
  const raw = useMessage((message) => message.metadata?.custom?.steps as Array<string | AssistantStep> | undefined)
  const [open, setOpen] = useState(false)
  if (!raw?.length) return null
  const steps = raw.map(normalizeStep)
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-shade-blue"
      >
        <ChevronRightIcon className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
        {open ? 'Hide steps' : `Show steps (${steps.length})`}
      </button>
      {open && <AssistantStepList steps={steps} className="mt-1" />}
    </div>
  )
}

function AssistantMessage() {
  const messageId = persistedMessageId(useMessage((message) => message.id))
  return (
    <MessagePrimitive.Root className="flex gap-2">
      <MessageExportCheckbox messageId={messageId} />
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-shade-blue/10 text-shade-blue">
        <ComputerDesktopIcon className="h-4 w-4" />
      </div>
      <div className="max-w-[85%] flex flex-col">
        <AssistantMessageSteps />
        <div className="rounded-2xl rounded-tl-sm bg-gray-100 px-3.5 py-2 text-sm text-gray-800">
          <MessagePrimitive.Content
            components={{
              Text: ({ text }) => <MarkdownRender text={text} />,
            }}
          />
        </div>
        <AssistantMessageActions messageId={messageId} />
      </div>
    </MessagePrimitive.Root>
  )
}

function AssistantMessageActions({ messageId }: { messageId: number }) {
  const exportCtx = useChatExportOptional()
  return (
    <ActionBarPrimitive.Root hideWhenRunning autohide="not-last" className="flex items-center gap-1 mt-1 ml-1">
      <ActionBarPrimitive.Copy asChild>
        <button
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title="Copy"
        >
          <ClipboardIcon className="h-3 w-3" />
        </button>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <button
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title="Regenerate"
        >
          <ArrowPathIcon className="h-3 w-3" />
        </button>
      </ActionBarPrimitive.Reload>
      {exportCtx && !exportCtx.selecting && (
        <ExportFormatMenu
          compact
          label="Save to documents"
          disabled={!exportCtx.canExport || !messageId}
          onPick={(format) => exportCtx.exportMessage(messageId, format)}
        />
      )}
    </ActionBarPrimitive.Root>
  )
}
